import { useMemo } from 'react';
import {
  getBondLevel,
  getBondProgress,
  getBondGreeting,
} from '../components/guideCatalogue';

export interface GuideBondState {
  /** Bond level 1-5 for this guide (1 when no record exists). */
  level: number;
  /** Days of bond for this guide specifically (0 when no record). */
  checkInDays: number;
  /** Progress within the current bond level — pct, daysToNext, etc. */
  progress: ReturnType<typeof getBondProgress>;
  /** Guide-specific greeting line for the current bond level, or null below level 2. */
  greeting: string | null;
}

/**
 * Per-guide bond state for the given guideId.
 *
 * Until Wave 3 Track E lands /api/me/guide-bonds, the data source is the
 * `perGuideBondDays` map passed in by the caller (App.tsx reads it from
 * the user profile JSON). After that endpoint lands, the hook can fetch
 * directly and the caller drops the prop.
 *
 * Fail-soft semantics: passing a null guideId or a guide without a record
 * returns level 1 / zero days / null greeting — never throws.
 */
export function useGuideBond(
  guideId: string | null,
  perGuideBondDays: Record<string, number> | null | undefined,
): GuideBondState {
  return useMemo(() => {
    const days = guideId && perGuideBondDays ? (perGuideBondDays[guideId] ?? 0) : 0;
    const level = getBondLevel(days);
    return {
      level,
      checkInDays: days,
      progress: getBondProgress(days),
      greeting: guideId ? getBondGreeting(guideId, level) : null,
    };
  }, [guideId, perGuideBondDays]);
}
